import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Alert, ErrorModal } from '@ibk/shared-ui';
import { extractErrorCode, getErrorMapping, getMessageByLanguage, interpolateMessage, } from '../exception-handler/exceptionHandler';
const ErrorHandlerContext = createContext(undefined);
function getTitleBySeverity(severity) {
    const titles = {
        error: 'Erro',
        warning: 'Atenção',
        info: 'Informação',
        success: 'Sucesso',
    };
    return titles[severity] ?? 'Notificação';
}
export function ErrorHandlerProvider({ children, dictionary, channel, language = 'DEFAULT', }) {
    const [notificationState, setNotificationState] = useState({
        toast: null,
        modal: null,
    });
    const showNotification = useCallback((errorMapping, customData) => {
        const selectedMessage = getMessageByLanguage(errorMapping.userMessage, language);
        const message = interpolateMessage(selectedMessage, customData);
        if (errorMapping.critical) {
            setNotificationState({
                toast: null,
                modal: {
                    isOpen: true,
                    title: getTitleBySeverity(errorMapping.severity),
                    message,
                    severity: errorMapping.severity,
                    actionable: errorMapping.actionable,
                    critical: true,
                },
            });
        }
        else {
            setNotificationState({
                modal: null,
                toast: {
                    isVisible: true,
                    message,
                    severity: errorMapping.severity,
                    actionable: errorMapping.actionable,
                },
            });
        }
    }, [language]);
    const handleError = useCallback((errorCode, customData) => {
        const errorMapping = getErrorMapping(dictionary, channel, errorCode);
        showNotification(errorMapping, customData);
    }, [dictionary, channel, showNotification]);
    const handleHttpError = useCallback((error) => {
        const errorCode = extractErrorCode(error);
        if (errorCode) {
            const errorObj = error;
            const customData = {};
            if (errorObj.response && typeof errorObj.response === 'object') {
                const response = errorObj.response;
                if (response.data && typeof response.data === 'object') {
                    Object.assign(customData, response.data);
                }
            }
            if (errorObj.details && typeof errorObj.details === 'object') {
                Object.assign(customData, errorObj.details);
            }
            handleError(errorCode, customData);
        }
        else {
            handleError('SYSTEM_001');
        }
    }, [handleError]);
    const closeToast = useCallback(() => {
        setNotificationState(prev => ({ ...prev, toast: null }));
    }, []);
    const closeModal = useCallback(() => {
        setNotificationState(prev => ({ ...prev, modal: null }));
    }, []);
    const contextValue = useMemo(() => ({
        handleError,
        handleHttpError,
    }), [handleError, handleHttpError]);
    return (_jsxs(ErrorHandlerContext.Provider, { value: contextValue, children: [children, notificationState.toast && (_jsx(Alert, { message: notificationState.toast.message, appearance: notificationState.toast.severity, alertType: 'toast', position: 'right', duration: notificationState.toast.severity === 'error' ? 4 : 5, onClose: closeToast, testIdIdentifier: 'error-toast' })), notificationState.modal && (_jsx(ErrorModal, { isOpen: notificationState.modal.isOpen, onClose: closeModal, title: notificationState.modal.title, message: notificationState.modal.message, severity: notificationState.modal.severity, actionable: notificationState.modal.actionable, critical: notificationState.modal.critical, "data-testid": 'error-modal' }))] }));
}
export function useExceptionHandler() {
    const context = useContext(ErrorHandlerContext);
    if (!context) {
        throw new Error('useExceptionHandler must be used within an ErrorHandlerProvider');
    }
    return context;
}
